import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Calculator, TrendingUp, Info, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const STORAGE_KEY = 'income_tax_data';

const IncomeTaxCalculator = () => {
  const [annualIncome, setAnnualIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).annualIncome : 1200000;
  });
  
  const [deduction80C, setDeduction80C] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).deduction80C : 150000;
  });
  
  const [deduction80D, setDeduction80D] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).deduction80D : 25000;
  });

  const [homeLoanInterest, setHomeLoanInterest] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).homeLoanInterest : 200000;
  });

  const [otherIncome, setOtherIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).otherIncome : 0;
  });

  const [age, setAge] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).age : 'below60';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      annualIncome,
      deduction80C,
      deduction80D,
      homeLoanInterest,
      otherIncome,
      age
    }));
  }, [annualIncome, deduction80C, deduction80D, homeLoanInterest, otherIncome, age]);

  const calculateOldRegime = () => {
    const income = parseFloat(annualIncome) || 0;
    const other = parseFloat(otherIncome) || 0;
    const grossIncome = income + other;
    
    const ded80C = Math.min(parseFloat(deduction80C) || 0, 150000);
    const ded80D = Math.min(parseFloat(deduction80D) || 0, age === 'senior' ? 50000 : 25000);
    const homeLoan = Math.min(parseFloat(homeLoanInterest) || 0, 200000);
    
    const totalDeductions = ded80C + ded80D + homeLoan;
    const taxableIncome = Math.max(0, grossIncome - totalDeductions);
    
    let basicExemption = 250000;
    if (age === 'senior') basicExemption = 300000;
    if (age === 'supersenior') basicExemption = 500000;
    
    const taxable = Math.max(0, taxableIncome - basicExemption);
    let tax = 0;
    
    if (taxable <= 250000) {
      tax = 0;
    } else if (taxable <= 500000) {
      tax = (taxable - 250000) * 0.05;
    } else if (taxable <= 1000000) {
      tax = 12500 + (taxable - 500000) * 0.20;
    } else {
      tax = 12500 + 100000 + (taxable - 1000000) * 0.30;
    }
    
    const cess = tax * 0.04;
    const totalTax = tax + cess;
    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;
    
    return {
      grossIncome,
      taxableIncome,
      totalDeductions,
      tax,
      cess,
      totalTax,
      effectiveRate,
      postTaxIncome: grossIncome - totalTax
    };
  };

  const calculateNewRegime = () => {
    const income = parseFloat(annualIncome) || 0;
    const other = parseFloat(otherIncome) || 0;
    const grossIncome = income + other;
    
    const basicExemption = 300000;
    const taxable = Math.max(0, grossIncome - basicExemption);
    
    let tax = 0;
    
    if (taxable <= 300000) {
      tax = 0;
    } else if (taxable <= 600000) {
      tax = (taxable - 300000) * 0.05;
    } else if (taxable <= 900000) {
      tax = 15000 + (taxable - 600000) * 0.10;
    } else if (taxable <= 1200000) {
      tax = 15000 + 30000 + (taxable - 900000) * 0.15;
    } else if (taxable <= 1500000) {
      tax = 15000 + 30000 + 45000 + (taxable - 1200000) * 0.20;
    } else {
      tax = 15000 + 30000 + 45000 + 60000 + (taxable - 1500000) * 0.30;
    }
    
    const cess = tax * 0.04;
    const totalTax = tax + cess;
    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;
    
    return {
      grossIncome,
      taxableIncome: grossIncome,
      totalDeductions: 0,
      tax,
      cess,
      totalTax,
      effectiveRate,
      postTaxIncome: grossIncome - totalTax
    };
  };

  const generateTaxSlabData = () => {
    const oldRegime = calculateOldRegime();
    const income = oldRegime.taxableIncome;
    
    const slabs = [
      { range: '0-2.5L', oldRate: '0%', newRate: '0%' },
      { range: '2.5L-5L', oldRate: '5%', newRate: '0%' },
      { range: '5L-6L', oldRate: '20%', newRate: '5%' },
      { range: '6L-9L', oldRate: '20%', newRate: '10%' },
      { range: '9L-10L', oldRate: '30%', newRate: '15%' },
      { range: '10L-12L', oldRate: '30%', newRate: '20%' },
      { range: '12L-15L', oldRate: '30%', newRate: '20%' },
      { range: '>15L', oldRate: '30%', newRate: '30%' }
    ];
    
    return slabs;
  };

  const generateRegimeComparison = () => {
    const old = calculateOldRegime();
    const newReg = calculateNewRegime();
    
    return [
      { 
        regime: 'Old Regime', 
        tax: Math.round(old.totalTax),
        postTax: Math.round(old.postTaxIncome)
      },
      { 
        regime: 'New Regime', 
        tax: Math.round(newReg.totalTax),
        postTax: Math.round(newReg.postTaxIncome)
      }
    ];
  };

  const generateIncomeBreakdown = (regime) => {
    const data = regime === 'old' ? calculateOldRegime() : calculateNewRegime();
    
    return [
      { name: 'Post-Tax Income', value: Math.round(data.postTaxIncome) },
      { name: 'Tax Paid', value: Math.round(data.totalTax) }
    ];
  };

  const generateIncomeTaxCurve = () => {
    const incomes = [300000, 500000, 800000, 1000000, 1200000, 1500000, 2000000, 2500000];
    
    return incomes.map(inc => {
      const oldIncome = annualIncome;
      setAnnualIncome(inc);
      const old = calculateOldRegime();
      const newReg = calculateNewRegime();
      setAnnualIncome(oldIncome);
      
      return {
        income: `$${(inc / 100000).toFixed(0)}L`,
        oldTax: Math.round(old.totalTax),
        newTax: Math.round(newReg.totalTax)
      };
    });
  };

  const oldRegime = calculateOldRegime();
  const newRegime = calculateNewRegime();
  const regimeComparison = generateRegimeComparison();
  const oldBreakdown = generateIncomeBreakdown('old');
  const newBreakdown = generateIncomeBreakdown('new');
  const taxSlabs = generateTaxSlabData();

  const COLORS = ['hsl(var(--secondary))', 'hsl(var(--destructive))', 'hsl(var(--accent))', 'hsl(var(--primary))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const betterRegime = oldRegime.totalTax < newRegime.totalTax ? 'old' : 'new';
  const savings = Math.abs(oldRegime.totalTax - newRegime.totalTax);

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Income Tax Calculator (India)
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Income Tax Calculator?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  This calculator helps you estimate your income tax liability under both Old and New tax regimes in India. 
                  Compare both regimes to choose the one that saves you more money based on your income and deductions.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Income Details</CardTitle>
                  <CardDescription>Enter your income and deductions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="annualIncome" className="text-sm font-medium">
                      Annual Salary Income ($)
                    </Label>
                    <Input
                      id="annualIncome"
                      type="number"
                      value={annualIncome}
                      onChange={(e) => setAnnualIncome(e.target.value)}
                      placeholder="1200000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otherIncome" className="text-sm font-medium">
                      Other Income ($)
                    </Label>
                    <Input
                      id="otherIncome"
                      type="number"
                      value={otherIncome}
                      onChange={(e) => setOtherIncome(e.target.value)}
                      placeholder="0"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Rental, interest, capital gains, etc.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-sm font-medium">
                      Age Category
                    </Label>
                    <Select value={age} onValueChange={setAge}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select age" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="below60">Below 60 years</SelectItem>
                        <SelectItem value="senior">60-80 years (Senior Citizen)</SelectItem>
                        <SelectItem value="supersenior">Above 80 years (Super Senior)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Deductions (Old Regime Only)</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="deduction80C" className="text-sm font-medium">
                        Section 80C ($)
                      </Label>
                      <Input
                        id="deduction80C"
                        type="number"
                        value={deduction80C}
                        onChange={(e) => setDeduction80C(e.target.value)}
                        placeholder="150000"
                        className="text-base"
                      />
                      <p className="text-xs text-muted-foreground">PPF, ELSS, Life Insurance (Max: $1.5L)</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="deduction80D" className="text-sm font-medium">
                        Section 80D ($)
                      </Label>
                      <Input
                        id="deduction80D"
                        type="number"
                        value={deduction80D}
                        onChange={(e) => setDeduction80D(e.target.value)}
                        placeholder="25000"
                        className="text-base"
                      />
                      <p className="text-xs text-muted-foreground">
                        Health Insurance (Max: {age === 'senior' ? '$50k' : '$25k'})
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="homeLoanInterest" className="text-sm font-medium">
                        Home Loan Interest ($)
                      </Label>
                      <Input
                        id="homeLoanInterest"
                        type="number"
                        value={homeLoanInterest}
                        onChange={(e) => setHomeLoanInterest(e.target.value)}
                        placeholder="200000"
                        className="text-base"
                      />
                      <p className="text-xs text-muted-foreground">Section 24 (Max: $2L)</p>
                    </div>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setAnnualIncome(annualIncome)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Tax
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Tax Calculation - Old Regime</CardTitle>
                    <CardDescription>With deductions under various sections</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Gross Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(oldRegime.grossIncome)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Deductions</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(oldRegime.totalDeductions)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Taxable Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(oldRegime.taxableIncome)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Tax Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(oldRegime.totalTax)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Effective Rate: {oldRegime.effectiveRate.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Tax Calculation - New Regime</CardTitle>
                    <CardDescription>Lower rates, no deductions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Taxable Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(newRegime.taxableIncome)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Tax Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(newRegime.totalTax)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Effective Rate: {newRegime.effectiveRate.toFixed(2)}%
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className={`p-6 rounded-xl bg-gradient-to-br ${betterRegime === 'old' ? 'from-secondary-light to-card border-secondary/20' : 'from-accent-light to-card border-accent/20'} border`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium">Recommended Regime:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          betterRegime === 'old' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'
                        }`}>
                          {betterRegime === 'old' ? 'Old Regime' : 'New Regime'}
                        </span>
                      </div>
                      <p className="text-2xl font-display font-bold mb-2">
                        Save {formatCurrency(savings)} annually
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {betterRegime === 'old' ? 
                          'Your deductions make the old regime more beneficial.' : 
                          'Lower tax rates in new regime outweigh your deductions.'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Old vs New Regime Comparison</CardTitle>
                <CardDescription className="text-base">
                  Visual comparison of tax liability and post-tax income
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={regimeComparison}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="regime" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="tax" fill="hsl(var(--destructive))" name="Tax Payable" />
                      <Bar dataKey="postTax" fill="hsl(var(--secondary))" name="Post-Tax Income" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Old Regime Income Distribution</CardTitle>
                  <CardDescription>
                    Breakdown of gross income
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={oldBreakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {oldBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">New Regime Income Distribution</CardTitle>
                  <CardDescription>
                    Breakdown of gross income
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={newBreakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {newBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Tax Slab Reference</CardTitle>
                <CardDescription className="text-base">
                  Income tax slabs for both regimes (FY 2024-25)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 font-semibold text-foreground">Income Range</th>
                        <th className="text-center p-3 font-semibold text-foreground">Old Regime Rate</th>
                        <th className="text-center p-3 font-semibold text-foreground">New Regime Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxSlabs.map((slab, index) => (
                        <tr key={index} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="p-3 text-muted-foreground">{slab.range}</td>
                          <td className="p-3 text-center font-semibold text-primary">{slab.oldRate}</td>
                          <td className="p-3 text-center font-semibold text-secondary">{slab.newRate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Income Tax in India</CardTitle>
                <CardDescription className="text-base">
                  Comprehensive guide to income tax calculation and optimization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Which Regime Should You Choose?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Based on your income of <strong className="text-primary">{formatCurrency(oldRegime.grossIncome)}</strong> and 
                    deductions of <strong className="text-primary">{formatCurrency(oldRegime.totalDeductions)}</strong>, 
                    the <strong className="text-primary">{betterRegime === 'old' ? 'Old Regime' : 'New Regime'}</strong> is 
                    more beneficial for you, saving <strong className="text-primary">{formatCurrency(savings)}</strong> annually.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <h4 className="font-semibold text-foreground mb-2">Old Regime Benefits:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Can claim multiple deductions</li>
                        <li>Better for high deduction claims</li>
                        <li>HRA, LTA exemptions available</li>
                        <li>Standard deduction of $50,000</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <h4 className="font-semibold text-foreground mb-2">New Regime Benefits:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Lower tax rates</li>
                        <li>Simpler tax calculation</li>
                        <li>Better for low deductions</li>
                        <li>No need to maintain proof</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Tax-Saving Opportunities
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💰 Section 80C ($1.5 Lakh)</h4>
                      <p className="text-muted-foreground text-sm">
                        Invest in PPF, ELSS mutual funds, life insurance, NSC, or repay home loan principal. 
                        These investments provide tax benefits and build wealth.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">💊 Section 80D ($25k-75k)</h4>
                      <p className="text-muted-foreground text-sm">
                        Health insurance for self, spouse, children ($25k) + parents ($25k/$50k for senior citizens). 
                        Preventive health check-up expenses also eligible ($5k).
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">🏠 Section 24 ($2 Lakh)</h4>
                      <p className="text-muted-foreground text-sm">
                        Deduct home loan interest up to $2 lakh for self-occupied property. 
                        No limit for let-out property. First-time buyers get additional $1.5L under 80EEA.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">📚 Other Deductions</h4>
                      <p className="text-muted-foreground text-sm">
                        80E (Education loan interest - no limit), 80G (Donations), 80TTA (Savings interest - $10k), 
                        80TTB (Senior citizen interest - $50k).
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Common Tax-Saving Mistakes to Avoid
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Not choosing the right regime:</strong> Evaluate both regimes annually. 
                        Your optimal choice may change with income and investment patterns.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Last-minute tax planning:</strong> Rushed investments often yield poor returns. 
                        Plan your tax-saving investments early in the financial year.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Ignoring NPS:</strong> National Pension System offers additional $50k deduction 
                        under 80CCD(1B), over and above 80C limit.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">❌</span>
                      <div>
                        <strong className="text-foreground">Not claiming HRA properly:</strong> If you pay rent and receive HRA, 
                        calculate and claim the exemption correctly to reduce taxable income.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Tax Filing Deadlines & Important Dates
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">📅 July 31</h4>
                      <p className="text-muted-foreground text-sm">
                        Last date to file ITR for salaried individuals and non-audit cases. Late filing attracts penalty of $5,000 ($1,000 for income below $5 lakh).
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">📅 March 31</h4>
                      <p className="text-muted-foreground text-sm">
                        Deadline for tax-saving investments under Section 80C and other deductions. 
                        Also, last date for completing advance tax payment to avoid interest charges.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">📅 December 31</h4>
                      <p className="text-muted-foreground text-sm">
                        Last date to file belated or revised return. After this, rectification is the only option available.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Post-Tax Income Planning
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your post-tax income under {betterRegime === 'old' ? 'old regime' : 'new regime'} is{' '}
                    <strong className="text-foreground">
                      {formatCurrency(betterRegime === 'old' ? oldRegime.postTaxIncome : newRegime.postTaxIncome)}
                    </strong>.
                    Here's how to plan your finances:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">50-30-20 Rule</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• 50% - Needs (rent, food, utilities)</li>
                        <li>• 30% - Wants (entertainment, dining)</li>
                        <li>• 20% - Savings & Investments</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">Emergency Fund</h4>
                      <p className="text-sm text-muted-foreground">
                        Maintain 6-12 months of expenses in liquid funds. 
                        Target: {formatCurrency((betterRegime === 'old' ? oldRegime.postTaxIncome : newRegime.postTaxIncome) / 2)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default IncomeTaxCalculator;
